import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { API } from '../App';
import { User, Menu, X } from 'lucide-react';
import Dashboard from '../components/admin/Dashboard';
import Orders from '../components/admin/Orders';
import Products from '../components/admin/Products';
import Users from '../components/admin/Users';
import PricingEditor from '../components/admin/PricingEditor';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    { id: 'orders', label: 'Orders', path: '/admin/orders' },
    { id: 'products', label: 'Products', path: '/admin/products' },
    { id: 'users', label: 'Users', path: '/admin/users' },
    { id: 'pricing', label: 'Pricing Editor', path: '/admin/pricing' },
  ];

  useEffect(() => {
    const path = window.location.pathname;
    const tab = tabs.find(t => t.path === path);
    if (tab) setActiveTab(tab.id);
  }, [window.location.pathname]);

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <Link to="/admin" className="header-logo">
            <img src="/logo.png" alt="InstallNow" />
            <span style={{ color: '#FDB515' }}>Admin</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav" style={{ display: 'flex', gap: '4px' }}>
            {tabs.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  background: activeTab === tab.id ? '#FDB515' : 'transparent',
                  color: activeTab === tab.id ? '#000' : '#666',
                  transition: 'all 0.2s ease'
                }}
                data-testid={`admin-nav-${tab.id}`}
              >
                {tab.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="header-actions desktop-actions">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="btn btn-secondary" data-testid="admin-account-menu">
                  <User size={16} />
                  {user.name}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onLogout} data-testid="admin-logout">
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#1A1A1A'
            }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="mobile-nav" style={{
            display: 'none',
            flexDirection: 'column',
            background: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            padding: '16px'
          }}>
            {tabs.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMobileMenuOpen(false);
                }}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  background: activeTab === tab.id ? '#FDB515' : 'transparent',
                  color: activeTab === tab.id ? '#000' : '#666',
                  marginBottom: '4px'
                }}
                data-testid={`admin-mobile-nav-${tab.id}`}
              >
                {tab.label}
              </Link>
            ))}
            <div style={{ borderTop: '1px solid #E0E0E0', margin: '12px 0' }} />
            <button
              onClick={() => {
                onLogout();
                setMobileMenuOpen(false);
              }}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '16px',
                color: '#EF4444',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>
        )}
      </header>

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
        <Routes>
          <Route path="/" element={<Dashboard user={user} />} />
          <Route path="/orders" element={<Orders user={user} />} />
          <Route path="/products" element={<Products user={user} />} />
          <Route path="/users" element={<Users user={user} />} />
          <Route path="/pricing" element={<PricingEditor user={user} />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
